import { CSSProperties, useState } from 'react'
import useMaestroChat from 'Hooks/useMaestroChat'
import Colors from 'modules/Colors'
import Icon from 'elements/Icon'

const DemoControls = () => {
    const { 
        demoMode,
        activateDemoScenario,
        exitDemoMode,
        getDemoScenarios 
    } = useMaestroChat()

    const [isHidden, setIsHidden] = useState(false)
    const [isCompletelyHidden, setIsCompletelyHidden] = useState(false)
    const [hideMessageActions, setHideMessageActions] = useState(false)

    // If completely hidden, show a minimal restore button
    if (!demoMode) {
        return null
    }

    if (isCompletelyHidden) {
        return (
            <button
                className="restoreFromCleanModeButton"
                style={sty.restoreFromCleanMode}
                onClick={() => setIsCompletelyHidden(false)}
                type="button"
                title="Restore Demo Controls"
            >
                <Icon className="restoreIcon" icon="views" size={12} color="white" />
            </button>
        )
    }

    if (isHidden) {
        return (
            <button
                className="showDemoControlsButton"
                style={sty.showButton}
                onClick={() => setIsHidden(false)}
                type="button"
                title="Show Demo Controls"
            >
                <Icon className="showDemoControlsIcon" icon="views" size={16} color="white" />
                <span style={sty.showButtonText}>Show Demo</span>
            </button>
        )
    }

    // Get primary demo scenarios only (removing duplicates and focusing on key demos)
    const primaryScenarios = [
        'video_generation',
        'marketing_plan_workspace',
        'ig_selector_workspace',
        'campaign_creation'
    ];

    // Define the list of workspaces and their modals for the new UI testing section
    const uiTestComponents = [
        { name: 'Workspace: Campaign Approval', scenarioId: 'ui_test_campaign_approval_workspace' },
        { name: 'Modal: Campaign Approval (Signup)', scenarioId: 'ui_test_campaign_approval_signup_modal' },
        { name: 'Modal: Campaign Approval (Subscription)', scenarioId: 'ui_test_campaign_approval_subscription_modal' },
        { name: 'Workspace: Content Analysis', scenarioId: 'ui_test_content_analysis_workspace' },
        { name: 'Workspace: Facebook Connect', scenarioId: 'ui_test_facebook_connect_workspace' },
        { name: 'Workspace: Instagram Post Selection', scenarioId: 'ui_test_instagram_post_selection_workspace' },
        { name: 'Modal: Instagram Post Selection (Cloudinary)', scenarioId: 'ui_test_instagram_post_selection_cloudinary_modal' },
        { name: 'Workspace: Marketing Plan', scenarioId: 'ui_test_marketing_plan_workspace' },
        { name: 'Workspace: Signup Flow', scenarioId: 'ui_test_signup_flow_workspace' }, // This is often modal content
        { name: 'Workspace: Subscription Flow', scenarioId: 'ui_test_subscription_flow_workspace' }, // This is often modal content
        { name: 'Workspace: Template Selection', scenarioId: 'ui_test_template_selection_workspace' },
        { name: 'Workspace: Website Preview', scenarioId: 'ui_test_website_preview_workspace' },
        // Add other workspaces and modals here as identified
    ];

    return (
        <div className="demoControls" style={sty.demoControls}>
            {/* Header with hide button */}
            <div className="demoControlsHeader" style={sty.demoControlsHeader}>
                <h3 style={sty.demoTitle}>Demo Mode Controls</h3>
                <div className="demoControlsHeaderButtons" style={sty.demoControlsHeaderButtons}>
                    <button
                        className="hideCompletelyButton"
                        style={sty.hideCompletelyButton}
                        onClick={() => setIsCompletelyHidden(true)}
                        type="button"
                        title="Hide Completely for Clean Screenshots (refresh page to show again)"
                    >
                        <Icon className="hideCompletelyIcon" icon="close" size={12} color="white" />
                        <span style={sty.hideCompletelyButtonText}>Clean Mode</span>
                    </button>
                    <button
                        className="hideDemoControlsButton"
                        style={sty.hideButton}
                        onClick={() => setIsHidden(true)}
                        type="button"
                        title="Hide for Screenshots (click the eye icon to show again)"
                    >
                        <Icon className="hideDemoControlsIcon" icon="close" size={14} color="white" />
                    </button>
                </div>
            </div>

            {/* UI Component Testing Section */}
            <div className="demoUiTestSection" style={sty.demoSection}>
                <h4 style={sty.sectionTitle}>UI Component Testing</h4>
                <div className="demoButtons" style={{ ...sty.demoButtons, maxHeight: 150, overflowY: 'auto' }}>
                    {uiTestComponents.map(component => (
                        <button
                            key={component.scenarioId}
                            className="demoButton"
                            style={{...sty.demoButtonSmall, backgroundColor: Colors.blue }}
                            onClick={() => activateDemoScenario(component.scenarioId, 'music')}
                            type="button"
                        >
                            {component.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Message Actions Toggle */}
            <div className="messageActionsToggle" style={sty.demoSection}>
                <h4 style={sty.sectionTitle}>Interface Options</h4>
                <div className="toggleContainer" style={sty.toggleContainer}>
                    <label style={sty.toggleLabel}>
                        <input
                            type="checkbox"
                            checked={hideMessageActions}
                            onChange={(e) => setHideMessageActions(e.target.checked)}
                            style={sty.toggleCheckbox}
                        />
                        <span style={sty.toggleText}>Hide Message Actions</span>
                    </label>
                </div>
            </div>

            {/* Primary Demo Scenarios */}
            <div className="demoPrimaryButtons" style={sty.demoSection}>
                <h4 style={sty.sectionTitle}>Quick Demo Scenarios</h4>
                <div className="demoButtons" style={sty.demoButtons}>
                    {primaryScenarios.map(scenarioId => {
                        const scenario = getDemoScenarios('music').find(s => s.id === scenarioId)
                        return scenario ? (
                            <button
                                key={scenario.id}
                                className="demoButton"
                                style={sty.demoButton}
                                onClick={() => activateDemoScenario(scenario.id, 'music')}
                                type="button"
                            >
                                {scenario.name}
                            </button>
                        ) : null
                    })}
                </div>
            </div>

            {/* Demo Type Variants */}
            <div className="demoVariants" style={sty.demoSection}>
                <h4 style={sty.sectionTitle}>Different Industries</h4>
                <div className="demoButtons" style={sty.demoButtons}>
                    <button
                        className="demoButton"
                        style={sty.demoButtonSmall}
                        onClick={() => activateDemoScenario('marketing_plan_workspace', 'ecommerce')}
                        type="button"
                    >
                        E-commerce Marketing
                    </button>
                    
                    <button
                        className="demoButton"
                        style={sty.demoButtonSmall}
                        onClick={() => activateDemoScenario('video_generation', 'content_creator')}
                        type="button"
                    >
                        Fitness Creator Video
                    </button>
                </div>
            </div>
                
            {/* Exit Demo Mode Button */}
            <button
                className="demoExitButton"
                style={sty.demoExitButton}
                onClick={exitDemoMode}
                type="button"
            >
                <Icon className="exitIcon" icon="close" size={12} color="white" />
                Exit Demo Mode
            </button>

            {/* Add hidden data attribute to control MessageActions visibility */}
            <div 
                data-hide-message-actions={hideMessageActions} 
                style={{ display: 'none' }}
            />
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    demoControls: {
        position: 'fixed',
        top: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        padding: 16,
        borderRadius: 12,
        zIndex: 9999,
        maxWidth: 320,
        maxHeight: 'calc(100vh - 40px)', // Ensure it doesn't go off-screen
        overflowY: 'auto', // Allow scrolling if content exceeds height
        border: `1px solid ${Colors.grey}`,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(15px)',
    },
    demoControlsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        paddingBottom: 8,
        borderBottom: `1px solid ${Colors.greyDark}`,
    },
    demoTitle: {
        color: Colors.white,
        fontSize: 14,
        fontWeight: 'bold',
        margin: 0,
    },
    demoControlsHeaderButtons: {
        display: 'flex',
        gap: 4,
    },
    hideButton: {
        background: Colors.greyDark,
        border: 'none',
        cursor: 'pointer',
        padding: 6,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    },
    hideCompletelyButton: {
        background: Colors.red,
        border: 'none',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        transition: 'background-color 0.2s ease',
    },
    hideCompletelyButtonText: {
        color: 'white',
        fontSize: 10,
        fontWeight: 500,
    },
    demoSection: {
        marginBottom: 16,
    },
    sectionTitle: {
        color: Colors.greyLight,
        fontSize: 11,
        fontWeight: 500,
        margin: '0 0 8px 0',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    toggleContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    toggleLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
    },
    toggleCheckbox: {
        accentColor: Colors.purple,
    },
    toggleText: {
        color: Colors.white,
        fontSize: 12,
        fontWeight: 400,
    },
    demoButtons: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    demoButton: {
        padding: '10px 14px',
        backgroundColor: Colors.purple,
        color: 'white',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        transition: 'all 0.2s ease',
        textAlign: 'left',
    },
    demoButtonSmall: {
        padding: '8px 12px',
        backgroundColor: Colors.greyDark,
        color: 'white',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 500,
        transition: 'all 0.2s ease',
        textAlign: 'left',
    },
    demoExitButton: {
        padding: '10px 14px',
        backgroundColor: Colors.red,
        color: 'white',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        marginTop: 8,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        justifyContent: 'center',
        width: '100%',
    },
    showButton: {
        position: 'fixed',
        top: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: 'none',
        borderRadius: 8,
        padding: '8px 12px',
        cursor: 'pointer',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    },
    showButtonText: {
        color: 'white',
        fontSize: 12,
        fontWeight: 500,
    },
    restoreFromCleanMode: {
        position: 'fixed',
        top: 20,
        right: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        border: 'none',
        borderRadius: 8,
        padding: '8px 12px',
        cursor: 'pointer',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
    },
}

export default DemoControls 